import {
  Bitmap,
  GlowFilter,
  MovieClip,
  Sprite,
  TextField,
  TextFormat,
  URLRequest,
  navigateToURL,
} from "../runtime";
import type { ContentRepository } from "../content/repository";
import type { LevelId, WorldMapEdgeDocument } from "../content/types";
import {
  G_bgCircuit,
  G_bgCircuit2,
  G_circuitBack,
  G_city,
  G_fortress,
  G_fortressShadow,
  G_levelTypeHolder,
  G_mapPoint,
  G_mapPointCrenson,
  G_mapPointCrenson2,
  G_mapPointSelected,
  M_title,
  whiteBlock,
} from "./assets";
import { Arrow } from "./Arrow";
import { NO_INPUT } from "./data/input";
import {
  MENU_ITEM_BOXES,
  MENU_ITEM_DEFAULTS,
  MENU_KINDS,
  MENU_ROW_SPACING,
  MENU_ROW_START,
  MENU_SCREENS,
  SOUND_STATE_TEXT,
} from "./data/menus";
import type { MenuBoxKind, MenuColour, MenuSelect } from "./data/menus";
import type { SceneName } from "./data/scenes";
import {
  ICON_OFFSETS,
  LEGACY_ROAD_ASSETS,
  selectableMapNodes,
} from "./data/worldMap";
import type { WorldMapDisplayNode } from "./data/worldMap";
import { Effect } from "./Effect";
import { EffectId } from "./ids";
import { Knight } from "./Knight";
import { Label } from "./Label";
import { FONT_BODY, TEXT_COLOURS, createTextField } from "./textStyle";

/** What Game should do with the menu after this frame. `goto` is "" to stay put. */
interface MenuUpdateResult {
  action: number;
  goto: SceneName | "";
  extraValue: LevelId | null;
  soundAction: number;
  gotoAction: number;
}

/** An armour/weapon chip tally Game hands over for the mission-select debrief. */
interface ChipData {
  /** 0 barrier wing, 1 megatron */
  readonly id: number;
  /** how many of the five pieces have been found */
  readonly qu: number;
}

/** Live state of one selectable option. */
interface MenuOption {
  id: number;
  choiceCurrent: number;
  choiceTotal: number;
  canPress: boolean;
  /** cycler widget this option drives, if it has one. */
  choice: MovieClip | null;
}

export class Menus extends MovieClip {
  guiState = -1;
  savedWeapon = 0;
  levelSelected: LevelId;
  knight: Knight | null = null;
  soundState = 1;
  cheatsOn = false;
  readonly chipDataArray: ChipData[] = [];
  private optionCurrent = 0;
  private readonly options: MovieClip[] = [];
  private readonly optionData: MenuOption[] = [];
  private readonly optionSelect: (MenuSelect | undefined)[] = [];
  private readonly pointerChoiceInput = NO_INPUT.slice();
  private dirPressed = false;
  private selectCounter = 0;
  private glowCounter = 0;
  private glowRising = false;
  private flashing = false;
  private flashCounter = 0;
  private flashOn = false;
  private soundAction = 0;
  private levelTypeHolder!: MovieClip;
  private levelTypeMine!: Effect;
  private levelTypeCleanser!: Effect;
  private levelTypeCrenson!: Bitmap;
  private levelTypeFinal!: Effect;
  private readonly effects: Effect[] = [];
  private gotoWebsite = false;
  private fortress: MovieClip | null = null;
  private fortressRising = false;
  private fortressSpeed = 0;
  private roadHolderMain!: MovieClip;
  private areaText!: Label;
  private baseExpanding = false;
  private background!: MovieClip;
  private readonly savedProgress: number;
  private readonly mapPoints = new Map<LevelId, MovieClip>();
  private action = 0;
  private readonly sceneType: SceneName;
  private knightBase: MovieClip | null = null;
  private readonly chipDataFormat = new TextFormat();
  private readonly survivalScore: number;
  private readonly survivalUnlockProgress: number;
  private readonly format = new TextFormat();
  private readonly content: ContentRepository;
  private readonly selectableNodes: readonly WorldMapDisplayNode[];
  private readonly cheatsUnlocked: boolean;

  constructor(
    content: ContentRepository,
    scene: SceneName,
    sWeapon: number,
    unlockedLevels: readonly LevelId[],
    selectedLevel: LevelId,
    sState: number,
    hyperArmour: boolean,
    sP: number,
    sS: number,
    cheats: { unlocked: boolean; on: boolean },
  ) {
    super();
    this.content = content;
    this.survivalScore = sS;
    this.savedProgress = sP;
    this.survivalUnlockProgress =
      content.worldMap.milestoneUnlocks.find(
        (milestone) => milestone.unlockSurvival,
      )?.next ?? Number.POSITIVE_INFINITY;
    this.sceneType = scene;
    this.savedWeapon = sWeapon;
    this.soundState = sState;
    this.cheatsUnlocked = cheats.unlocked;
    this.cheatsOn = cheats.on;
    this.selectableNodes = selectableMapNodes(content.worldMap, unlockedLevels);
    const fallbackLevel = this.selectableNodes[0]?.id;
    if (fallbackLevel == null) throw new Error("menu has no selectable level");
    this.levelSelected = this.selectableNodes.some(
      (node) => node.id === selectedLevel,
    )
      ? selectedLevel
      : fallbackLevel;
    this.format.size = 24;
    this.format.bold = true;
    if (scene === "title" || scene === "main menu") {
      this.buildTitleScreen(scene);
    }
    if (scene === "level select") {
      this.buildLevelSelect(hyperArmour);
    }
    this.optionCycle();
  }

  update(input: readonly boolean[]): MenuUpdateResult {
    let goto: SceneName | "" = "";
    let gotoAction = 0;
    let extraValue: LevelId | null = null;
    const previousOption = this.optionCurrent | 0;
    if (this.knightBase != null) {
      this.knightBase.rotation += this.knightBase.scaleX * 1.5;
    }
    if (this.guiState === 0) {
      this.readInput(input);
    }
    // `=== false` and not `!input[n]`: Game hands over an empty array while a
    // dialog is up, and the original's `== false` did not fire on undefined
    if (
      input[0] === false &&
      input[1] === false &&
      input[2] === false &&
      input[3] === false
    ) {
      this.dirPressed = false;
    }
    if (previousOption !== this.optionCurrent) {
      this.optionCycle();
    }
    this.updateGlow();
    if (this.baseExpanding) {
      this.knightBase!.scaleX += (12 - this.knightBase!.scaleX) * 0.015;
      this.knightBase!.scaleY = this.knightBase!.scaleX;
    }
    if (this.flashing) {
      ++this.flashCounter;
      if (this.flashCounter === 2) {
        this.flashOn = !this.flashOn;
        this.flashCounter = 0;
      }
      ++this.selectCounter;
      if (this.selectCounter === 15) {
        this.action = 1;
        const select = this.optionSelect[this.optionCurrent];
        if (select != null) {
          goto = select.goto ?? "";
          gotoAction = select.gotoAction ?? 0;
          if (select.passChoice) {
            extraValue = this.levelSelected;
          }
          if (select.website) {
            this.gotoWebsite = true;
          }
        }
      }
      if (this.selectCounter === 26) {
        this.flashOn = false;
        this.flashCounter = 0;
        this.selectCounter = 0;
        this.flashing = false;
        if (this.gotoWebsite) {
          this.gotoWebsite = false;
          navigateToURL(new URLRequest("http://www.maxgames.com/"), "_blank");
          this.guiState = 0;
        }
      }
    }
    if (this.sceneType === "level select") {
      this.fortressEvents();
      this.levelTypeMine.updateAlpha();
      this.levelTypeCleanser.updateAlpha();
      this.levelTypeFinal.updateAlpha();
      if (this.knight != null) {
        const emptyArray: never[] = [];
        this.knight.update(NO_INPUT, emptyArray, emptyArray, false);
      }
    }
    this.effectCycle();
    const r: MenuUpdateResult = {
      action: this.action,
      goto: goto,
      extraValue: extraValue,
      soundAction: this.soundAction,
      gotoAction: gotoAction,
    };
    this.soundAction = 0;
    this.action = 0;
    return r;
  }
  /** Selects and activates the menu control under a stage-space pointer. */
  pointerDown(stageX: number, stageY: number): boolean {
    if (this.guiState !== 0) return false;
    for (let optionIndex = 0; optionIndex < this.optionData.length; optionIndex++) {
      const option = this.optionData[optionIndex];
      const choice = option.choice;
      if (choice == null || !choice.hitTestPoint(stageX, stageY)) continue;
      const bounds = choice.$localBounds();
      if (!bounds) return false;
      this.focusOption(optionIndex);
      const matrix = choice.$concatenatedMatrix();
      const centerX = bounds.x + bounds.width * 0.5;
      const centerY = bounds.y + bounds.height * 0.5;
      const stageCenterX = matrix.a * centerX + matrix.c * centerY + matrix.tx;
      this.moveChoiceFromPointer(option, stageX >= stageCenterX);
      return true;
    }
    for (
      let optionIndex = 0;
      optionIndex < this.options.length;
      optionIndex++
    ) {
      if (!this.options[optionIndex].hitTestPoint(stageX, stageY)) continue;
      this.focusOption(optionIndex);
      this.pressOption(this.optionData[optionIndex]);
      return true;
    }
    return false;
  }

  getSoundText(): string {
    return SOUND_STATE_TEXT[this.soundState] ?? SOUND_STATE_TEXT[1];
  }

  titleEvents(): void {
    this.background.x += 1;
    if (this.background.x >= 0) {
      this.background.x -= 1024;
    }
  }

  setChipData(): void {
    if (this.chipDataArray.length === 0) {
      return;
    }
    this.chipDataFormat.size = 12;
    this.chipDataFormat.bold = true;
    this.chipDataFormat.font = FONT_BODY;
    this.chipDataFormat.color = TEXT_COLOURS.aqua;
    this.chipDataFormat.align = "right";
    for (
      let chipIndex = 0;
      chipIndex < this.chipDataArray.length;
      chipIndex++
    ) {
      const chipData = this.chipDataArray[chipIndex];
      const y = 405 - chipIndex * 19;
      this.addChipLabel(
        425,
        y,
        TEXT_COLOURS.white,
        chipData.id === 0 ? "BARRIER WING:" : "MEGATRON:",
      );
      this.addChipLabel(
        514,
        y,
        TEXT_COLOURS.aqua,
        chipData.qu < 5 ? `${chipData.qu} / 5` : "COMPLETE!",
      );
    }
  }

  private buildTitleScreen(scene: SceneName): void {
    this.background = new MovieClip();
    this.addChild(this.background);
    const circuitBack = new Sprite();
    circuitBack.graphics.beginBitmapFill(new G_circuitBack());
    circuitBack.graphics.drawRect(0, 0, 2000, 480);
    circuitBack.graphics.endFill();
    this.background.addChild(circuitBack);
    const isTitle = scene === "title";
    this.addChild(new Bitmap(isTitle ? new G_bgCircuit() : new G_bgCircuit2()));
    const xenosTitle = new Bitmap(new M_title());
    xenosTitle.y = isTitle ? 310 : 25;
    this.addChild(xenosTitle);
    this.buildScreen(scene);
    this.addPortCredit();
  }

  private buildLevelSelect(hyperArmour: boolean): void {
    const bgMap = new Bitmap(new G_city());
    bgMap.cacheAsBitmap = true;
    this.addChild(bgMap);
    const fortressShadowSpr = new Bitmap(new G_fortressShadow(0, 0));
    fortressShadowSpr.x = fortressShadowSpr.width * -0.5 + 567;
    fortressShadowSpr.y = fortressShadowSpr.height * -0.5 + 216;
    this.addChild(fortressShadowSpr);
    this.fortress = new MovieClip();
    this.fortress.x = 567;
    this.fortress.y = 155;
    this.addChild(this.fortress);
    const fortressSpr = new Bitmap(new G_fortress(0, 0));
    fortressSpr.x = fortressSpr.width * -0.5;
    fortressSpr.y = fortressSpr.height * -0.5;
    this.fortress.addChild(fortressSpr);
    this.roadHolderMain = new MovieClip();
    this.roadHolderMain.blendMode = "add";
    this.roadHolderMain.alpha = 0.55;
    this.addChild(this.roadHolderMain);
    const selectableIds = new Set(this.selectableNodes.map((node) => node.id));
    for (const edge of this.content.worldMap.edges) {
      if (selectableIds.has(edge.from) && selectableIds.has(edge.to)) {
        this.addMapEdge(edge);
      }
    }
    for (const node of this.selectableNodes) {
      this.generateMapNode(node);
    }
    this.knightBase = new MovieClip();
    this.addChild(this.knightBase);
    const kbSpr = new Bitmap(new G_mapPointSelected());
    kbSpr.x = kbSpr.width * -0.5;
    kbSpr.y = kbSpr.height * -0.5;
    kbSpr.smoothing = true;
    this.knightBase.addChild(kbSpr);
    this.knightBase.blendMode = "add";
    this.knight = new Knight(
      720,
      480,
      -250,
      -250,
      [],
      this.savedWeapon,
      false,
      [2, 2, 2, 2, 2],
      hyperArmour,
    );
    this.knight.stuck = true;
    this.addChild(this.knight);
    this.levelHighlighted(this.levelSelected);
    this.buildScreen("level select");
    this.areaText = new Label(14, 162, 105);
    this.addChild(this.areaText);
    this.levelTypeHolder = new MovieClip();
    this.levelTypeHolder.x = 384;
    this.levelTypeHolder.y = 132;
    this.addChild(this.levelTypeHolder);
    this.levelTypeMine = new Effect(EffectId.Mine);
    this.levelTypeHolder.addChild(this.levelTypeMine);
    this.levelTypeCleanser = new Effect(EffectId.Cleanser);
    this.levelTypeHolder.addChild(this.levelTypeCleanser);
    this.levelTypeCrenson = new Bitmap(new G_mapPointCrenson2());
    this.levelTypeCrenson.x = this.levelTypeCrenson.width * -0.5;
    this.levelTypeCrenson.y = this.levelTypeCrenson.height * -0.5;
    this.levelTypeHolder.addChild(this.levelTypeCrenson);
    this.levelTypeFinal = new Effect(EffectId.SmallFinalBossEffect);
    this.levelTypeHolder.addChild(this.levelTypeFinal);
    const lHolderSpr = new Bitmap(new G_levelTypeHolder());
    lHolderSpr.x = lHolderSpr.width * -0.5;
    lHolderSpr.y = lHolderSpr.height * -0.5;
    this.levelTypeHolder.addChild(lHolderSpr);
    this.levelTypeHolder.visible = false;
    this.getAreaText();
  }

  /** Places one scene's entries, handing out option indices in table order. */
  private buildScreen(scene: SceneName): void {
    let rowY = MENU_ROW_START;
    for (const entry of MENU_SCREENS[scene] ?? []) {
      if (
        entry.survivalOnly &&
        this.savedProgress < this.survivalUnlockProgress
      ) {
        continue;
      }
      if (entry.cheatsOnly && !this.cheatsUnlocked) {
        continue;
      }
      const y = entry.stacked ? rowY + entry.y : entry.y;
      const label = this.resolveLabel(entry.text);
      this.generateText(entry.x, y, label, entry.kind, entry.colour);
      if (MENU_KINDS[entry.kind].role === "option") {
        this.optionSelect.push(entry.select);
      }
      if (entry.endsRow) {
        rowY += MENU_ROW_SPACING;
      }
    }
  }

  private resolveLabel(text: string): string {
    switch (text) {
      case "{sound}":
        return this.getSoundText();
      case "{cheats}":
        return this.cheatsOn ? "ON" : "OFF";
      case "{score}":
        return String(this.survivalScore);
      case "{level}":
        return this.selectedNode().label;
      default:
        return text;
    }
  }

  private readInput(input: readonly boolean[]): void {
    if (this.options.length > 1) {
      let optionChanged = false;
      if (!this.dirPressed) {
        if (input[0]) {
          --this.optionCurrent;
          this.dirPressed = true;
          this.soundAction = 1;
          optionChanged = true;
        }
        if (input[1]) {
          ++this.optionCurrent;
          this.dirPressed = true;
          this.soundAction = 1;
          optionChanged = true;
        }
      }
      if (this.optionCurrent < 0) {
        this.optionCurrent = this.options.length - 1;
      }
      if (this.optionCurrent >= this.options.length) {
        this.optionCurrent = 0;
      }
      if (optionChanged) {
        this.action = this.optionCurrent + 10;
      }
    }
    const option = this.optionData[this.optionCurrent];
    if (input[4]) this.pressOption(option);
    if (option.id >= 1 && option.choiceTotal > 1) {
      this.cycleChoice(input, option);
    }
  }
  private focusOption(optionIndex: number): void {
    if (optionIndex === this.optionCurrent) return;
    this.optionCurrent = optionIndex;
    this.optionCycle();
    this.action = optionIndex + 10;
    this.soundAction = 1;
  }

  private pressOption(option: MenuOption): void {
    if (!option.canPress) return;
    this.guiState = -1;
    this.flashing = true;
    this.flashCounter = 0;
    this.flashOn = true;
    this.soundAction = 3;
    if (this.optionSelect[this.optionCurrent]?.expandBase) {
      this.baseExpanding = true;
    }
  }

  private moveChoiceFromPointer(option: MenuOption, right: boolean): void {
    this.dirPressed = false;
    const slot = right ? 3 : 2;
    this.pointerChoiceInput[slot] = true;
    this.cycleChoice(this.pointerChoiceInput, option);
    this.pointerChoiceInput[slot] = false;
    this.dirPressed = false;
  }

  private cycleChoice(input: readonly boolean[], option: MenuOption): void {
    const choice = option.choice;
    if (choice == null) return;
    const previousChoice = option.choiceCurrent | 0;
    if (!this.dirPressed) {
      let choiceChanged = false;
      if (input[2]) {
        --option.choiceCurrent;
        (choice.getChildAt(0) as Arrow).press();
        this.dirPressed = true;
        choiceChanged = true;
        this.soundAction = 2;
      }
      if (input[3]) {
        ++option.choiceCurrent;
        (choice.getChildAt(1) as Arrow).press();
        this.dirPressed = true;
        choiceChanged = true;
        this.soundAction = 2;
      }
      if (option.choiceCurrent < 1) {
        option.choiceCurrent = option.choiceTotal;
      }
      if (option.choiceCurrent > option.choiceTotal) {
        option.choiceCurrent = 1;
      }
      if (choiceChanged && option.id === 1) {
        const selected = this.selectableNodes[option.choiceCurrent - 1];
        if (selected == null) throw new Error("invalid mission selection");
        this.levelSelected = selected.id;
        this.levelHighlighted(selected.id);
        this.action = 2;
        this.getAreaText();
      }
    }
    if (previousChoice === option.choiceCurrent) {
      return;
    }
    const label = choice.getChildAt(2) as TextField;
    switch (option.id) {
      case 1:
        label.text = this.selectedNode().label;
        break;
      case 2:
        this.soundState = option.choiceCurrent;
        label.text = this.getSoundText();
        this.action = 2;
        break;
      case 3:
        this.cheatsOn = option.choiceCurrent === 2;
        label.text = this.cheatsOn ? "ON" : "OFF";
        this.action = 2;
        break;
    }
  }

  private addChipLabel(
    x: number,
    y: number,
    colour: number,
    text: string,
  ): void {
    this.chipDataFormat.color = colour;
    const t = createTextField(this, this.chipDataFormat, {
      width: 195,
      height: 30,
      x,
      y,
      text,
    });
    t.filters = [new GlowFilter(613, 1, 3, 3, 4, 1)];
  }

  private fortressEvents(): void {
    if (this.fortress != null) {
      this.fortress.y += this.fortressSpeed * 0.005;
      if (this.fortressRising) {
        --this.fortressSpeed;
        if (this.fortressSpeed <= -42) {
          this.fortressRising = false;
        }
      } else {
        ++this.fortressSpeed;
        if (this.fortressSpeed > 50) {
          this.fortressRising = true;
        }
      }
    }
  }

  private effectCycle(): void {
    for (const effect of this.effects) {
      effect.updateAlpha();
    }
  }

  private optionCycle(): void {
    for (
      let optionIndex = 0;
      optionIndex < this.options.length;
      optionIndex++
    ) {
      this.options[optionIndex].getChildAt(1)!.filters = null;
      this.options[optionIndex].getChildAt(0)!.visible =
        optionIndex === this.optionCurrent;
    }
    this.glowCounter = 15;
    this.glowRising = false;
  }

  private levelHighlighted(levelId: LevelId): void {
    const point = this.mapPoints.get(levelId);
    if (point == null) return;
    this.knightBase!.x = point.x;
    this.knightBase!.y = point.y;
    this.knight!.x = point.x;
    this.knight!.y = point.y - 14;
  }

  private generateText(
    x: number,
    y: number,
    displayText: string,
    menuBoxType: MenuBoxKind,
    colour: MenuColour = "white",
  ): void {
    const kind = MENU_KINDS[menuBoxType];
    let txtWidth = kind.txtWidth;
    let txtY = kind.txtY;
    const option: MenuOption = {
      id: MENU_ITEM_DEFAULTS.optionId,
      choiceCurrent: 1,
      choiceTotal: MENU_ITEM_DEFAULTS.choiceTotal,
      canPress: MENU_ITEM_DEFAULTS.canPress,
      choice: null,
    };
    const th = new MovieClip();
    this.addChild(th);
    if (menuBoxType === "title item") {
      // stand-in for the highlight bitmap so the glow still lands on child 1
      th.addChild(new MovieClip());
    }
    if (menuBoxType === "menu item") {
      const box = MENU_ITEM_BOXES[displayText]!;
      const bitHighlight = new Bitmap(new box.highlight());
      bitHighlight.x = box.highlightX ?? MENU_ITEM_DEFAULTS.highlightX;
      bitHighlight.y = box.highlightY ?? MENU_ITEM_DEFAULTS.highlightY;
      th.addChild(bitHighlight);
      bitHighlight.visible = false;
      th.addChild(new Bitmap(new box.box()));
      txtWidth = box.txtWidth ?? MENU_ITEM_DEFAULTS.txtWidth;
      txtY = box.txtY ?? MENU_ITEM_DEFAULTS.txtY;
      option.id = box.optionId ?? MENU_ITEM_DEFAULTS.optionId;
      const total = box.choiceTotal ?? MENU_ITEM_DEFAULTS.choiceTotal;
      option.choiceTotal =
        total === "unlocked" ? this.selectableNodes.length : total;
      const current = box.choiceCurrent ?? MENU_ITEM_DEFAULTS.choiceCurrent;
      if (current === "selected") {
        option.choiceCurrent =
          this.selectableNodes.findIndex(
            (node) => node.id === this.levelSelected,
          ) + 1;
      } else if (current === "cheats") {
        option.choiceCurrent = this.cheatsOn ? 2 : 1;
      } else {
        option.choiceCurrent = current;
      }
      option.canPress = box.canPress ?? MENU_ITEM_DEFAULTS.canPress;
    }
    if (kind.arrows != null) {
      const [arrowDist, arrowOffsetY] = kind.arrows;
      for (let arrowIndex = 0; arrowIndex < 2; arrowIndex++) {
        th.addChild(new Arrow(arrowIndex, arrowDist, txtWidth, arrowOffsetY));
      }
    }
    th.x = x;
    th.y = y;
    if (kind.role === "option") {
      this.options.push(th);
      this.optionData.push(option);
    } else if (kind.role === "choice") {
      // the cycler always follows the `menu item` it belongs to
      const owner = this.optionData[this.optionData.length - 1];
      if (owner == null) throw new Error("menu cycler has no option");
      owner.choice = th;
    }
    this.format.font = kind.font;
    this.format.size = kind.size;
    this.format.align = kind.align;
    this.format.color = TEXT_COLOURS[colour];
    // GO BACK and MISSION SELECT have their caption baked into the box bitmap
    if (displayText !== "GO BACK" && displayText !== "MISSION SELECT") {
      createTextField(th, this.format, {
        width: txtWidth,
        height: 64,
        y: txtY,
        text: displayText,
      });
    }
  }

  // Replaces the sponsor/developer logos that used to sit on these screens.
  // Deliberately not routed through generateText(), which would register it as
  // a selectable menu option and shift every option index.
  private addPortCredit(): void {
    const creditFormat = new TextFormat();
    creditFormat.font = FONT_BODY;
    creditFormat.size = 12;
    creditFormat.color = TEXT_COLOURS.mutedAqua;
    creditFormat.align = "center";
    createTextField(this, creditFormat, {
      width: 720,
      height: 24,
      x: 0,
      y: 452,
      text: "A port of the original Xenos game by Oddity, published on MaxGames in 2011",
      wordWrap: false,
    });
  }

  private updateGlow(): void {
    if (this.glowRising) {
      ++this.glowCounter;
      if (this.glowCounter === 15) {
        this.glowRising = false;
      }
    } else {
      --this.glowCounter;
      if (this.glowCounter <= 0) {
        this.glowRising = true;
      }
    }
    let glowSize = this.glowCounter * 0.4 + 3;
    let currentColour = 9471;
    if (this.flashOn) {
      currentColour = 5438975;
      glowSize *= 1.5;
    }
    const glowFilter = new GlowFilter(
      currentColour,
      1,
      glowSize,
      glowSize,
      2,
      1,
    );
    this.options[this.optionCurrent].getChildAt(0)!.alpha =
      this.glowCounter * 0.032 + 0.4;
    this.options[this.optionCurrent].getChildAt(1)!.filters = [glowFilter];
  }

  private generateMapNode(node: WorldMapDisplayNode): void {
    const pointHolder = new MovieClip();
    pointHolder.x = node.x * 12 + 6;
    pointHolder.y = node.y * 12 + 6;
    const mapPointSpr = new Bitmap(new G_mapPoint());
    mapPointSpr.x = mapPointSpr.width * -0.5;
    mapPointSpr.y = mapPointSpr.height * -0.5;
    this.addChild(pointHolder);
    pointHolder.addChild(mapPointSpr);
    this.mapPoints.set(node.id, pointHolder);
    if (node.mission === "crenson" || node.mission === "final") {
      this.addMissionIcon(node, pointHolder.x, pointHolder.y);
    }
  }

  private addMapEdge(edge: WorldMapEdgeDocument): void {
    const from = this.content.node(edge.from);
    const to = this.content.node(edge.to);
    if (edge.road == null) {
      this.addVectorRoad(from, to);
      return;
    }
    const roadHolder = new MovieClip();
    roadHolder.x = from.x * 12 + 6;
    roadHolder.y = from.y * 12 + 6;
    roadHolder.rotation = edge.road.rotate ?? 0;
    if (edge.road.flipX) roadHolder.scaleX = -1;
    if (edge.road.flipY) roadHolder.scaleY = -1;
    const RoadAsset = LEGACY_ROAD_ASSETS[edge.road.asset];
    const roadSegmentSpr = new Bitmap(new RoadAsset());
    roadSegmentSpr.x = -3;
    roadSegmentSpr.y = 3 - roadSegmentSpr.height;
    roadHolder.addChild(roadSegmentSpr);
    this.roadHolderMain.addChild(roadHolder);
  }

  private addVectorRoad(
    from: WorldMapDisplayNode,
    to: WorldMapDisplayNode,
  ): void {
    const x = from.x * 12 + 6;
    const y = from.y * 12 + 6;
    const dx = (to.x - from.x) * 12;
    const dy = (to.y - from.y) * 12;
    const road = new Sprite();
    road.x = x;
    road.y = y;
    road.rotation = (Math.atan2(dy, dx) * 180) / Math.PI;
    road.graphics.beginBitmapFill(new whiteBlock());
    road.graphics.drawRect(0, -2, Math.hypot(dx, dy), 4);
    road.graphics.endFill();
    this.roadHolderMain.addChild(road);
  }

  private addMissionIcon(
    node: WorldMapDisplayNode,
    x: number,
    y: number,
  ): void {
    const [iconOffsetX, iconOffsetY] =
      node.iconSide == null ? [0, 0] : ICON_OFFSETS[node.iconSide];
    if (node.mission === "crenson") {
      const missionCrenson = new Bitmap(new G_mapPointCrenson());
      missionCrenson.x = x + iconOffsetX - missionCrenson.width * 0.5;
      missionCrenson.y = y + iconOffsetY - missionCrenson.height * 0.5;
      this.addChild(missionCrenson);
    }
    if (node.mission === "final") {
      const fBossIcon = new Effect(
        EffectId.XenosShard,
        x + iconOffsetX,
        y + iconOffsetY,
      );
      this.addChild(fBossIcon);
      this.effects.push(fBossIcon);
    }
  }

  private selectedNode(): WorldMapDisplayNode {
    const node =
      this.selectableNodes.find(
        (candidate) => candidate.id === this.levelSelected,
      ) ?? this.selectableNodes[0];
    if (node == null) throw new Error("mission select has no unlocked level");
    return node;
  }

  private getAreaText(): void {
    const node = this.selectedNode();
    const mission = node.mission;
    this.levelTypeHolder.visible = mission != null;
    if (mission != null) {
      this.levelTypeMine.visible = mission === "mine";
      this.levelTypeCleanser.visible = mission === "cleanser";
      this.levelTypeCrenson.visible = mission === "crenson";
      this.levelTypeFinal.visible = mission === "final";
    }
    this.areaText.updateText(node.area);
  }
}
